/**
 * UI Slice
 * 
 * Manages UI-related state (modals, sidebars, loading states, etc.)
 */

import { createSlice } from '@reduxjs/toolkit';

// Initial state
const initialState = {
  sidebarOpen: true,
  modals: {
    addOfficer: false,
    editOfficer: false,
    addComplaint: false,
    editComplaint: false,
    confirmDialog: false,
  },
  activeTab: 'inbox',
  theme: 'light',
  loadingStates: {},
  toast: {
    show: false,
    message: '',
    type: 'info', // 'success' | 'error' | 'warning' | 'info'
  },
};

// UI slice
const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    // Sidebar
    toggleSidebar: (state) => {
      state.sidebarOpen = !state.sidebarOpen;
    },
    setSidebarOpen: (state, action) => {
      state.sidebarOpen = action.payload;
    },

    // Modals
    openModal: (state, action) => {
      if (state.modals.hasOwnProperty(action.payload)) {
        state.modals[action.payload] = true;
      }
    },
    closeModal: (state, action) => {
      if (state.modals.hasOwnProperty(action.payload)) {
        state.modals[action.payload] = false;
      }
    },
    closeAllModals: (state) => {
      Object.keys(state.modals).forEach(key => {
        state.modals[key] = false;
      });
    },

    // Active Tab
    setActiveTab: (state, action) => {
      state.activeTab = action.payload;
    },

    // Theme
    setTheme: (state, action) => {
      state.theme = action.payload;
    },
    toggleTheme: (state) => {
      state.theme = state.theme === 'light' ? 'dark' : 'light';
    },

    // Loading States
    setLoading: (state, action) => {
      const { key, value } = action.payload;
      state.loadingStates[key] = value;
    },
    clearLoading: (state, action) => {
      delete state.loadingStates[action.payload];
    },
    clearAllLoading: (state) => {
      state.loadingStates = {};
    },

    // Toast/Notification
    showToast: (state, action) => {
      state.toast = {
        show: true,
        message: action.payload.message,
        type: action.payload.type || 'info',
      };
    },
    hideToast: (state) => {
      state.toast.show = false;
    },
  },
});

// Export actions
export const {
  toggleSidebar,
  setSidebarOpen,
  openModal,
  closeModal,
  closeAllModals,
  setActiveTab,
  setTheme,
  toggleTheme,
  setLoading,
  clearLoading,
  clearAllLoading,
  showToast,
  hideToast,
} = uiSlice.actions;

// Export selectors
export const selectSidebarOpen = (state) => state.ui.sidebarOpen;
export const selectModals = (state) => state.ui.modals;
export const selectModalOpen = (modalName) => (state) => state.ui.modals[modalName];
export const selectActiveTab = (state) => state.ui.activeTab;
export const selectTheme = (state) => state.ui.theme;
export const selectLoading = (key) => (state) => state.ui.loadingStates[key] || false;
export const selectToast = (state) => state.ui.toast;

// Export reducer
export default uiSlice.reducer;

